import React from "react";
import { ConfigProvider } from "antd";
import { useTranslation } from "react-i18next";
import "./App.css";
import { RTL_LANGUAGES } from "./i18n/config";
import Login from "./pages/Login";
import Register from "./pages/Register";
import { Routes, Route } from "react-router-dom";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AITranslation from "./components/AITools/AITranslation/AITranslationList";
import AddAITranslation from "./components/AITools/AITranslation/AddAIToolsTranslation";
import AISurveyGenerator from "./components/AITools/SurveyGenerator/GenerateSurvey";
import PageLayout from "./components/PageLayout";
// FormBuilder/SurveyBuilder is no longer used — commented out (see App.tsx routes below)
// import SurveyBuilder from "./components/FormBuilder";
import Workspaces from "./components/Workspaces";
import { ToastContainer } from "react-toastify";
import ProjectsList from "./components/Projects/ProjectsList";
import MembersList from "./components/UserManagement/MembersList";
import ProjectDashboard from "./components/Projects/ProjectDashboard/ProjectDashboard";
import ProfileInformation from "./components/AccountSettings/ProfileInformation";
import ProtectedRoute from "./hooks/protectedRoute";
import Unauthorized from "./hooks/unauthorized";
// import UploadSurveyPage from "./components/FormBuilder/UploadSurveyPage";
import GoogleDrive from "./components/GoogleDriveModule/GoogleDrive";
import DocumentProcessing from "./components/DocProcessing/DocProcessingList";
import FormulaBuilderPage from "./components/DocProcessing/Analyze/FormulaBuilder/FormulaBuilderPage";
import SubscriptionPage from "./components/Subscription/SubscriptionPage";
import DashboardLayout from "./components/Dashboard/DashboardLayout";
import DashboardPreview from "./components/Dashboard/DashboardPreview";
import PublicDashboardView from "./components/Dashboard/PublicDashboardView";
import PublicTranslation from "./components/AITools/AITranslation/PublicTranslation";
import DataCleaningPage from "./components/DataCleaning/DataCleaningPage";
import DataReviewPage from "./components/DataCleaning/DataReviewPage";
import HelpCenterPage from "./components/HelpCenter/HelpCenterPage";
import AuditTrails from "./components/AuditTrails";
import ProjectHub from "./components/ProjectHub";
import BalancedScorecardPage from "./components/BalancedScorecard/BalancedScorecardPage";
import BalancedScorecardListPage from "./components/BalancedScorecard/BalancedScorecardListPage";
import PublicBalancedScorecardHistoryView from "./components/BalancedScorecard/PublicBalancedScorecardHistoryView";
import LibraryPage from "./components/Library/LibraryPage";
import DataAnonymisationPage from "./components/DataAnonymisation/DataAnonymisationPage";
import ReportingPage from "./components/Reporting/ReportingPage";
import ReportBuilder from "./components/Reporting/ReportBuilder";

const App: React.FC = () => {
  const { i18n } = useTranslation();
  const isRTL = RTL_LANGUAGES.includes(i18n.language.split("-")[0]);

  return (
    <ConfigProvider direction={isRTL ? "rtl" : "ltr"}>
      <div className="app-container">
      <div className="content">
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route
            path="/share/dashboard/:token"
            element={<PublicDashboardView />}
          />
          <Route
            path="/share/bsc/:token"
            element={<PublicBalancedScorecardHistoryView />}
          />
          <Route
            path="/public/ai-translation"
            element={<PublicTranslation />}
          />
          <Route path="/signup" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route element={<PageLayout />}>
            {/* <Route
              path="/home"
              element={
                <ProtectedRoute
                  allowedRoles={["Admin", "Guest", "Project Manager"]}
                  redirectPath="/unauthorized"
                />
              }
            >
              <Route path="" element={<Home />} />
            </Route> */}
            <Route
              path="/profile-information"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    "Admin",
                    "Guest",
                    "Technical Expert",
                    "Data Collector",
                    "Translation People",
                    "Quality Assurance",
                    "Project Manager",
                  ]}
                  redirectPath="/unauthorized"
                />
              }
            >
              <Route path="" element={<ProfileInformation />} />
            </Route>
            <Route
              path="/subscription-plans"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    "Admin",
                    "Guest",
                    "Technical Expert",
                    "Data Collector",
                    "Translation People",
                    "Quality Assurance",
                    "Project Manager",
                  ]}
                  redirectPath="/unauthorized"
                />
              }
            >
              <Route path="" element={<SubscriptionPage />} />
            </Route>
            <Route
              path="/upload-file-to-google-drive/:id"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    "Admin",
                    "Guest",
                    "Technical Expert",
                    "Data Collector",
                    "Translation People",
                    "Quality Assurance",
                    "Project Manager",
                  ]}
                  redirectPath="/unauthorized"
                />
              }
            >
              <Route path="" element={<GoogleDrive />} />
            </Route>

            <Route
              path="/ai-translation/:id"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    "Admin",
                    "Project Manager",
                    "Technical Expert",
                    "Translation People",
                    "Quality Assurance",
                    "Guest",
                  ]}
                  redirectPath="/unauthorized"
                />
              }
            >
              <Route path="" element={<AITranslation />} />
            </Route>
            <Route
              path="/data-cleaning/:id"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    "Admin",
                    "Project Manager",
                    "Technical Expert",
                    "Translation People",
                    "Quality Assurance",
                    "Guest",
                  ]}
                  redirectPath="/unauthorized"
                />
              }
            >
              <Route path="" element={<DataCleaningPage />} />
            </Route>
            <Route
              path="/data-cleaning/:id/review/:datasetId"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    "Admin",
                    "Project Manager",
                    "Technical Expert",
                    "Translation People",
                    "Quality Assurance",
                    "Guest",
                  ]}
                  redirectPath="/unauthorized"
                />
              }
            >
              <Route path="" element={<DataReviewPage />} />
            </Route>
            <Route
              path="/help-center"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    "Admin",
                    "Project Manager",
                    "Technical Expert",
                    "Translation People",
                    "Quality Assurance",
                    "Guest",
                  ]}
                  redirectPath="/unauthorized"
                />
              }
            >
              <Route path="" element={<HelpCenterPage />} />
            </Route>
            <Route
              path="/audit-trails/:id"
              element={
                <ProtectedRoute
                  allowedRoles={["Admin", "Project Manager"]}
                  redirectPath="/unauthorized"
                />
              }
            >
              <Route path="" element={<AuditTrails />} />
            </Route>
            <Route
              path="/project-hub/:id"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    "Admin",
                    "Project Manager",
                    "Technical Expert",
                    "Translation People",
                    "Quality Assurance",
                    "Guest",
                  ]}
                  redirectPath="/unauthorized"
                />
              }
            >
              <Route path="" element={<ProjectHub />} />
            </Route>
            <Route
              path="/library/:id"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    "Admin",
                    "Project Manager",
                    "Technical Expert",
                    "Translation People",
                    "Quality Assurance",
                    "Guest",
                  ]}
                  redirectPath="/unauthorized"
                />
              }
            >
              <Route path="" element={<LibraryPage />} />
            </Route>
            <Route
              path="/data-anonymisation/:id"
              element={
                <ProtectedRoute
                  allowedRoles={["Admin", "Project Manager"]}
                  redirectPath="/unauthorized"
                />
              }
            >
              <Route path="" element={<DataAnonymisationPage />} />
            </Route>
            <Route
              path="/balanced-scorecard/:id"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    "Admin",
                    "Project Manager",
                    "Technical Expert",
                    "Translation People",
                    "Quality Assurance",
                    "Guest",
                  ]}
                  redirectPath="/unauthorized"
                />
              }
            >
              <Route path="" element={<BalancedScorecardListPage />} />
              <Route path=":scorecardId" element={<BalancedScorecardPage />} />
            </Route>
            <Route
              path="/dashboard/:id"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    "Admin",
                    "Project Manager",
                    "Technical Expert",
                    "Translation People",
                    "Quality Assurance",
                    "Guest",
                  ]}
                  redirectPath="/unauthorized"
                />
              }
            >
              <Route path="" element={<DashboardLayout />} />
            </Route>
            <Route
              path="/dashboard/preview/:id"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    "Admin",
                    "Project Manager",
                    "Technical Expert",
                    "Translation People",
                    "Quality Assurance",
                    "Guest",
                  ]}
                  redirectPath="/unauthorized"
                />
              }
            >
              <Route path="" element={<DashboardPreview />} />
            </Route>
            <Route
              path="/reporting/:id"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    "Admin",
                    "Project Manager",
                    "Technical Expert",
                    "Translation People",
                    "Quality Assurance",
                    "Guest",
                  ]}
                  redirectPath="/unauthorized"
                />
              }
            >
              <Route path="" element={<ReportingPage />} />
            </Route>
            <Route
              path="/reporting/:id/builder"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    "Admin",
                    "Project Manager",
                    "Technical Expert",
                    "Translation People",
                    "Quality Assurance",
                    "Guest",
                  ]}
                  redirectPath="/unauthorized"
                />
              }
            >
              <Route path="" element={<ReportBuilder />} />
            </Route>
            <Route
              path="/reporting/:id/builder/:reportId"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    "Admin",
                    "Project Manager",
                    "Technical Expert",
                    "Translation People",
                    "Quality Assurance",
                    "Guest",
                  ]}
                  redirectPath="/unauthorized"
                />
              }
            >
              <Route path="" element={<ReportBuilder />} />
            </Route>
            <Route
              path="/doc-processing/:id"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    "Admin",
                    "Project Manager",
                    "Technical Expert",
                    "Translation People",
                    "Quality Assurance",
                    "Guest",
                  ]}
                  redirectPath="/unauthorized"
                />
              }
            >
              <Route path="" element={<DocumentProcessing />} />
            </Route>
            <Route
              path="/formula-builder/:id"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    "Admin",
                    "Project Manager",
                    "Technical Expert",
                    "Translation People",
                    "Quality Assurance",
                    "Guest",
                  ]}
                  redirectPath="/unauthorized"
                />
              }
            >
              <Route path="" element={<FormulaBuilderPage />} />
            </Route>
            <Route
              path="/add-ai-translation/:id"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    "Admin",
                    "Project Manager",
                    "Technical Expert",
                    "Guest",
                  ]}
                  redirectPath="/unauthorized"
                />
              }
            >
              <Route path="" element={<AddAITranslation />} />
            </Route>
            <Route
              path="/ai-survey-generator/:id"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    "Admin",
                    "Project Manager",
                    "Technical Expert",
                    "Data Collector",
                    "Guest",
                  ]}
                  redirectPath="/unauthorized"
                />
              }
            >
              <Route path="" element={<AISurveyGenerator />} />
            </Route>
            {/* <Route
              path="/deleted-items"
              element={
                <ProtectedRoute
                  allowedRoles={["Admin", "Project Manager"]}
                  redirectPath="/unauthorized"
                />
              }
            >
              <Route path="" element={<DeletedAiTranslation />} />
            </Route> */}
            {/* FormBuilder is no longer used — routes commented out
            <Route
              path="/upload-survey/:id"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    "Admin",
                    "Project Manager",
                    "Data Collector",
                    "Guest",
                  ]}
                  redirectPath="/unauthorized"
                />
              }
            >
              <Route path="" element={<UploadSurveyPage />} />
            </Route>
            <Route
              path="/survey-builder/:id"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    "Admin",
                    "Project Manager",
                    "Data Collector",
                    "Guest",
                  ]}
                  redirectPath="/unauthorized"
                />
              }
            >
              <Route path="" element={<SurveyBuilder />} />
            </Route>
            */}
            <Route
              path="/workspaces"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    "Admin",
                    "Guest",
                    "Technical Expert",
                    "Data Collector",
                    "Translation People",
                    "Quality Assurance",
                    "Project Manager",
                  ]}
                  redirectPath="/unauthorized"
                />
              }
            >
              <Route path="" element={<Workspaces />} />
            </Route>

            <Route
              path="/projects/:workspaceId"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    "Admin",
                    "Project Manager",
                    "Technical Expert",
                    "Data Collector",
                    "Translation People",
                    "Quality Assurance",
                    "Guest",
                  ]}
                  redirectPath="/unauthorized"
                />
              }
            >
              <Route path="" element={<ProjectsList />} />
            </Route>
            <Route
              path="/projects/:id/details"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    "Admin",
                    "Project Manager",
                    "Technical Expert",
                    "Data Collector",
                    "Translation People",
                    "Quality Assurance",
                    "Guest",
                  ]}
                  redirectPath="/unauthorized"
                />
              }
            >
              <Route path="" element={<ProjectDashboard />} />
            </Route>
            <Route
              path="/members"
              element={
                <ProtectedRoute allowedRoles={["Admin", "Project Manager"]} />
              }
            >
              <Route path="" element={<MembersList />} />
            </Route>
          </Route>
        </Routes>
      </div>
      <ToastContainer />
      </div>
    </ConfigProvider>
  );
};

export default App;
